import React, { useEffect } from "react";
import { SubmitHandler } from 'react-hook-form';
import { FiX } from "react-icons/fi";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  handleSubmit?: (callback: SubmitHandler<any>) => (e: React.FormEvent<HTMLFormElement>) => void;
  closeRequest?: (formData: any) => void;
  title?: string;
  isFooterAvailable?: boolean;
  children: React.ReactNode;
  backButton?: string;
  primaryButton?: string;
  modalClassName?: string;
  modalOverflowClassName?: string;
  modalBodyClassName?: string;
  isXAvailable?: boolean;
}

const ModalDailog: React.FC<ModalProps> = ({ isOpen, onClose, handleSubmit, closeRequest, backButton, primaryButton, title, modalClassName, modalBodyClassName, modalOverflowClassName, isFooterAvailable = true, isXAvailable = true, children }) => {
  // Effect to handle scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const onSubmit: SubmitHandler<any> = async (formData) => {
    if (closeRequest) {
      await closeRequest(formData); // Call closeRequest with form data
    } else {
      onClose(); // Close the modal after submitting
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-gray-900 bg-opacity-50 z-30 size-full flex justify-center items-center overflow-x-hidden overflow-y-auto"
      >
        {/* Modal */}
        <div className={`ease-out transition-all md:max-w-2xl lg:max-w-4xl content-center md:w-full fixed z-50 m-3 h-[calc(100%-3.5rem)] sm:mx-auto 
          ${isOpen === true ? 'mt-7 opacity-100 duration-500' : "opacity-0 mt-0 "} ${modalClassName}`}>
          <div
            className={`max-h-full flex flex-col bg-white border shadow-sm rounded-xl pointer-events-auto ${modalOverflowClassName}`}
            onClick={(e) => e.stopPropagation()}
          >
            <form
              name="closeForm"
              onSubmit={handleSubmit ? handleSubmit(onSubmit) : (e) => { e.preventDefault(); onClose() }}
              noValidate
            >
              {/* Modal Header */}
              <div className="modal-header p-4 2xl:p-5 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-base 2xl:text-lg font-semibold">{title}</h3>
                  {isXAvailable && (
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      <FiX size={24} />
                    </button>
                  )}
                </div>
              </div>
              {/* Modal Content */}
              <div className={`modal-body p-4 2xl:p-5 ${modalBodyClassName}`} >{children}</div>
              {/* Modal Footer */}
              {isFooterAvailable && (
                <div className="modal-footer p-4 2xl:p-5 border-t flex justify-end space-x-4">
                  {backButton && (<button
                    onClick={onClose}
                    className="btn btn-outline-gray py-2"
                  >
                    {backButton}
                  </button>)}
                  {primaryButton && (<button className="btn btn-primary py-2" type="submit">
                    {primaryButton}
                  </button>)}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalDailog;
